import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './App.css';
import Documents from './pages/Documents';
import WellbeingCentredCare from './pages/WellbeingCentredCare';
import ServantLeadership from './pages/ServantLeadership';
import CQCRegistration from './pages/CQCRegistration';
import ContactUs from './pages/ContactUs';
import CareSuites from './pages/CareSuites';
import Activities from './pages/Activities';
import MeetTheTeam from './pages/MeetTheTeam';
import PhotoGallery from './pages/PhotoGallery';
import RespiteCare from './pages/RespiteCare';
import DementiaCare from './pages/DementiaCare';
import NursingCare from './pages/NursingCare';
import QualityAssurance from './pages/QualityAssurance';
import GMBUnion from './pages/GMBUnion';
import SelfFunding from './pages/SelfFunding';
import SocialServicesFunding from './pages/SocialServicesFunding';
import NHSFunding from './pages/NHSFunding';
import WorkWithUs from './pages/WorkWithUs';
import HelpAndAdvice from './pages/HelpAndAdvice';
import FundingCareHomeFees from './pages/FundingCareHomeFees';
import GuideChoosingCareHome from './pages/GuideChoosingCareHome';
import UnderstandingDifferentStagesDementia from './pages/UnderstandingDifferentStagesDementia';
import IsItTimeForCareHome from './pages/IsItTimeForCareHome';
import WhatIsPersonCentredCare from './pages/WhatIsPersonCentredCare';
import HowPalliativeCareFundedUk from './pages/HowPalliativeCareFundedUk';
import WhatToPackMovingCareHomeChecklist from './pages/WhatToPackMovingCareHomeChecklist';
import GuideMovingHospitalCareHome from './pages/GuideMovingHospitalCareHome';
import IntroducingProactiveRehabCentre from './pages/IntroducingProactiveRehabCentre';
import Testimonials from './pages/Testimonials';
import PhoneCalls from './pages/PhoneCalls';
import Monitoring from './pages/Monitoring';
import RmOnline from './pages/RmOnline';
import WeeklyMenus from './pages/WeeklyMenus';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import { isArchived } from './config/archivedPages';

function Home() {
  // Array of image paths for the slideshow
  const heroImages = [
    '/harpwood_images/harpwood-002-Edit-scaled.jpg',
    '/harpwood_images/harpwood-034-Edit-1-scaled.jpg',
    '/harpwood_images/VEC609_002-1-scaled.jpg',
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('fade-in');

  useEffect(() => {
    // Only run slideshow if there are multiple images
    if (heroImages.length <= 1) return;

    const interval = setInterval(() => {
      setFadeClass('fade-out');
      
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
        setFadeClass('fade-in');
      }, 500); // Half of transition duration (1s = 1000ms, half = 500ms)
    }, 10000); // Change image every 10 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <>
    <Helmet>
      <title>Harpwood House Care Home | Nursing & Dementia Care Suites</title>
      <meta
        name="description"
        content="Harpwood House offers luxury 27m² Care Suites for residential, nursing & dementia care. Located next to Estuary View GP. Rated 'Good' by CQC"
      />
      <link rel="canonical" href="https://www.harpwood.house/" />
    </Helmet>
    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content">
        <section className="hero">
          <div 
            className={`hero__image ${fadeClass}`} 
            style={{ backgroundImage: `url('${heroImages[currentImageIndex]}')` }}
          ></div>
          <div className="hero__content">
            <h1 className="hero__title">
              <span>Welcome to</span>
              <span className="hero__title-main">Harpwood House</span>
            </h1>
            <div className="hero__contact-icons">
              <a href="tel:01732494555" className="hero__contact-icon" aria-label="Call us">
                <span className="icon-phone">📞</span>
              </a>
              <a href="mailto:enquiries@harpwood.house" className="hero__contact-icon" aria-label="Email us">
                <span className="icon-email">✉️</span>
              </a>
            </div>
            <Link to="/contact-us" className="btn-enquiry">New resident enquiry</Link>
          </div>
        </section>

        <section className="content-section introduction">
          <div className="section__container">
            <div className="section__text">
              <p>Harpwood House is a newly developed care home, offering residential, nursing care to people with care needs, particularly those with physical frailty or dementia-type illnesses.</p>
            </div>
          </div>
        </section>

        <section className="content-section care-suites">
          <div className="section__container">
            <div className="section__text">
              <h2>Care Suites</h2>
              <p>The home consists of our larger-than-average Care Suites that come equipped with an en-suite bathroom, kitchenette and living and dining area. They are purpose-built with residents and their families in mind, each room encouraging independence as well as visits from loved ones.</p>
              <p>Learn more about our care suites <Link to="/care-suites" className="nurse-care-link">here</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/harpwood_images/VEC609_015-1-scaled.jpg" alt="Care Suites" />
            </div>
          </div>
        </section>

        <section className="content-section quality-facilities">
          <div className="section__container">
            <div className="section__text">
              <h2>Purpose-Built, Quality Facilities</h2>
              <p>Built with its residents at the heart of its design, Harpwood House offers high quality, care-orientated facilities, creating the ideal environment for our residents to feel safe and at home in. What’s more, the home is situated in quiet and beautiful natural surroundings in Sevenoaks, Kent.</p>
              <p>View our brochure <a href="https://grahamcare.sharepoint.com/:b:/s/WebDocuments/IQCmI5Yf2JFyToIX_MWKy6PnAQApVwca4hxZRmVDirjOpiI?e=zBwTd2" className="nurse-care-link" target="_blank" rel="noopener noreferrer">here</a>.</p>
            </div>
            <div className="section__image">
              <img src="/harpwood_images/harpwood-059-scaled.jpg" alt="Harpwood House purpose-built facilities and grounds" />
            </div>
          </div>
        </section>

        <section className="content-section serving-community">
          <div className="section__container">
            <div className="section__text">
              <h2>Serving the Community</h2>
              <p>Residents at Harpwood House reflect the demographic of the surrounding area. We facilitate families to benefit from Local Authority or NHS funding, where private funding may not be an option, making our services accessible to the whole community.</p>
              <p>Learn more about our different funding options, whether it's <Link to="/self-funding" className="nurse-care-link">self-funding</Link>, <Link to="/social-services-funding" className="nurse-care-link">social services funding</Link> or <Link to="/nhs-funding" className="nurse-care-link">NHS funding</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/harpwood_images/VEC609_014-scaled.jpg" alt="Harpwood House serving the local community" />
            </div>
          </div>
        </section>

        <section className="content-section care-services">
          <div className="section__container">
            <div className="section__text">
              <h2>Long-Term and Short-Term Care Services</h2>
              <p>Harpwood House is a 'home for life' for those who require long term care, whilst also offering short term care for others who require rehabilitation, assessment, or respite before returning home.</p>
              <p>Read more about <Link to="/respite-care" className="nurse-care-link">respite</Link> care.</p>
            </div>
            <div className="section__image">
              <img src="/harpwood_images/VEC609_012-1-scaled.jpg" alt="Long-Term and Short-Term Care Services" />
            </div>
          </div>
        </section>

        <section className="content-section nurse-led-care">
          <div className="section__container">
            <div className="section__text">
              <h2>24/7 Nurse-Led Care</h2>
              <p>Our experienced and trained nurses are always on-hand to support our residents. They lead and oversee each resident's care at every step of their care journeys, from initial assessment, to reviews of the care plan, to their personalised social programme.</p>
              <p>Read more about our approach to <Link to="/nursing-care" className="nurse-care-link">nursing care</Link> and <Link to="/dementia-care" className="nurse-care-link">dementia care</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/iStock-1165981632-scaled.jpg" alt="24/7 Nurse-Led Care" />
            </div>
          </div>
        </section>

        <section className="content-section relationship-care">
          <div className="section__container">
            <div className="section__text">
              <h2>Wellbeing Centred Care</h2>
              <p>We believe that a person's wellbeing is more than the sum of their physical needs — it is about living meaningfully, comfortably, and with dignity, even in the context of frailty or cognitive decline.</p>
              <p>Read more about Wellbeing Centred Care <Link to="/wellbeing-centred-care" className="nurse-care-link">here</Link>.</p>
            </div>
            <div className="section__image">
              <img src="/pillars-of-wellbeing.jpg" alt="Wellbeing Centred Care" />
            </div>
          </div>
        </section>

        <section className="content-section care-choices-badge" aria-label="Care Choices badge">
          <div className="care-choices-badge__inner">
            <img
              src="/Care-Choices_web-badges-06.png"
              alt="Care Choices web badge"
              className="care-choices-badge__image"
            />
          </div>
        </section>

      </main>

      <Footer />
    </div>
    </>
  );
}

function NotFound() {
  return (
    <div className="page">
      <Header />
      <main id="main-content" style={{ padding: '4rem 1.5rem', textAlign: 'center' }}>
        <h1>Page not found</h1>
        <p>This page is not available. It may have been moved or is not yet published.</p>
        <Link to="/">Return to home</Link>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        {!isArchived('/documents') && <Route path="/documents" element={<Documents />} />}
        {!isArchived('/wellbeing-centred-care') && <Route path="/wellbeing-centred-care" element={<WellbeingCentredCare />} />}
        {!isArchived('/testimonials') && <Route path="/testimonials" element={<Testimonials />} />}
        {!isArchived('/servant-leadership') && <Route path="/servant-leadership" element={<ServantLeadership />} />}
        {!isArchived('/cqc-registration') && <Route path="/cqc-registration" element={<CQCRegistration />} />}
        {!isArchived('/contact-us') && <Route path="/contact-us" element={<ContactUs />} />}
        <Route path="/phone-calls" element={<PhoneCalls />} />
        <Route path="/monitoring" element={<Monitoring />} />
        <Route path="/rmonline" element={<RmOnline />} />
        {!isArchived('/care-suites') && <Route path="/care-suites" element={<CareSuites />} />}
        {!isArchived('/activities') && <Route path="/activities" element={<Activities />} />}
        {!isArchived('/weekly-menus') && <Route path="/weekly-menus" element={<WeeklyMenus />} />}
        {!isArchived('/meet-the-team') && <Route path="/meet-the-team" element={<MeetTheTeam />} />}
        {!isArchived('/photo-gallery') && <Route path="/photo-gallery" element={<PhotoGallery />} />}
        {!isArchived('/respite-care') && <Route path="/respite-care" element={<RespiteCare />} />}
        {!isArchived('/dementia-care') && <Route path="/dementia-care" element={<DementiaCare />} />}
        {!isArchived('/nursing-care') && <Route path="/nursing-care" element={<NursingCare />} />}
        {!isArchived('/quality-assurance') && <Route path="/quality-assurance" element={<QualityAssurance />} />}
        {!isArchived('/gmb-union') && <Route path="/gmb-union" element={<GMBUnion />} />}
        {!isArchived('/self-funding') && <Route path="/self-funding" element={<SelfFunding />} />}
        {!isArchived('/social-services-funding') && <Route path="/social-services-funding" element={<SocialServicesFunding />} />}
        {!isArchived('/nhs-funding') && <Route path="/nhs-funding" element={<NHSFunding />} />}
        {!isArchived('/work-with-us') && <Route path="/work-with-us" element={<WorkWithUs />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/funding-care-home-fees-whitstable" element={<FundingCareHomeFees />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/guide-choosing-care-home-whitstable" element={<GuideChoosingCareHome />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/understanding-different-stages-dementia" element={<UnderstandingDifferentStagesDementia />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/is-it-time-for-care-home" element={<IsItTimeForCareHome />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/what-is-person-centred-care" element={<WhatIsPersonCentredCare />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/how-palliative-care-funded-uk" element={<HowPalliativeCareFundedUk />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/what-to-pack-moving-care-home-checklist" element={<WhatToPackMovingCareHomeChecklist />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/guide-moving-hospital-care-home" element={<GuideMovingHospitalCareHome />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice/post/introducing-whitstable-house-proactive-rehab-centre" element={<IntroducingProactiveRehabCentre />} />}
        {!isArchived('/help-and-advice') && <Route path="/help-and-advice" element={<HelpAndAdvice />} />}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
